import type { SpecificityOptionKey } from '@massilia/queries'

import {
  SPECIFICITY_OPTIONS,
  useCreateBookingSpecificityMutation,
  useCreateSessionSpecificityMutation,
} from '@massilia/queries'
import { Button, Checkbox, Textarea } from '@massilia/ui'
import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface SpecificitySectionProps {
  bookingId?: string
  sessionId?: string
}

export function SpecificitySection({ bookingId, sessionId }: SpecificitySectionProps) {
  const [checkedOptions, setCheckedOptions] = useState<Set<SpecificityOptionKey>>(new Set())
  const [freeText, setFreeText] = useState('')
  const [freeTextError, setFreeTextError] = useState<null | string>(null)
  const [submitted, setSubmitted] = useState(false)

  const bookingMutation = useCreateBookingSpecificityMutation()
  const sessionMutation = useCreateSessionSpecificityMutation()

  const isPending = bookingMutation.isPending || sessionMutation.isPending

  const toggleOption = (key: SpecificityOptionKey) => {
    setCheckedOptions((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
        if (key === 'TEXTE_LIBRE') setFreeTextError(null)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleSubmit = () => {
    if (checkedOptions.size === 0) return

    if (checkedOptions.has('TEXTE_LIBRE') && !freeText.trim()) {
      setFreeTextError('Veuillez décrire vos besoins spécifiques')
      return
    }

    const options = Array.from(checkedOptions)
    const payload = {
      options,
      ...(checkedOptions.has('TEXTE_LIBRE') && freeText.trim()
        ? { freeText: freeText.trim() }
        : {}),
    }

    const onSuccess = () => {
      setSubmitted(true)
      toast.success('Spécificités envoyées avec succès')
    }

    const onError = () => {
      toast.error("Erreur lors de l'envoi des spécificités")
    }

    if (bookingId) {
      bookingMutation.mutate({ ...payload, bookingId }, { onError, onSuccess })
    } else if (sessionId) {
      sessionMutation.mutate({ ...payload, sessionId }, { onError, onSuccess })
    }
  }

  if (submitted) {
    return (
      <div className="border-gray-light-200 flex items-center gap-3 rounded-2xl border bg-white p-6">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
          <Check className="h-4 w-4 text-green-600" />
        </div>
        <div>
          <div className="text-gray-light-900 text-sm font-semibold">Spécificités envoyées</div>
          <p className="text-gray-light-600 text-xs">
            Notre équipe a bien reçu vos demandes et vous recontactera si nécessaire.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="border-gray-light-200 flex flex-col gap-4 rounded-2xl border bg-white p-6">
      <div>
        <div className="text-gray-light-900 text-lg font-semibold">Spécificités</div>
        <p className="text-gray-light-600 mt-1 text-sm">
          Signalez-nous vos besoins spécifiques pour ce voyage. Votre demande sera transmise à notre
          équipe directement et celle-ci vous contactera rapidement.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {SPECIFICITY_OPTIONS.map((option) => (
          <label className="flex cursor-pointer items-center gap-3" key={option.key}>
            <Checkbox
              checked={checkedOptions.has(option.key)}
              onCheckedChange={() => toggleOption(option.key)}
            />
            <span className="text-gray-light-700 text-sm">{option.label}</span>
          </label>
        ))}
      </div>

      {checkedOptions.has('TEXTE_LIBRE') && (
        <div className="flex flex-col gap-1">
          <Textarea
            className={`mt-1${freeTextError ? ' border-red-500' : ''}`}
            onChange={(e) => {
              setFreeText(e.target.value)
              if (freeTextError) setFreeTextError(null)
            }}
            placeholder="Décrivez vos besoins spécifiques..."
            rows={3}
            value={freeText}
          />
          {freeTextError && (
            <p className="text-sm text-red-500">{freeTextError}</p>
          )}
        </div>
      )}

      <div>
        <Button
          disabled={checkedOptions.size === 0 || isPending}
          onClick={handleSubmit}
          type="button"
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Envoyer
        </Button>
      </div>
    </div>
  )
}
